import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';

import AuthUtil from '../../AuthUtil';
import { EMPLOYEE_SESSION_V2 } from '../../../components/global/Server/endpoints';
import { showErrorMessage } from '../../actions/global';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../pages/Dashboard/constants';
import {
  LOAD_SESSION_REPORTS,
  EDIT_BONUS_V2,
  DELETE_BONUS_V2,
  ADD_BONUS_V2,
  SUBMIT_BONUS_DECISION,
  EDIT_RAFFLE_V2,
  DELETE_RAFFLE_V2,
  ADD_RAFFLE_V2,
  SUBMIT_RAFFLE_DECISION,
  EDIT_TICKETOUT_V2,
  DELETE_TICKETOUT_V2,
  EDIT_TRANSACTION_V2,
  DELETE_TRANSACTION_V2,
  ADD_TRANSACTION_V2,
  EDIT_CASH_LEFT_V2,
  LOAD_AUDIT_LOG,
  LOAD_OWNER_AUDIT_SETTINGS,
  SAVE_OWNER_AUDIT_SETTINGS,
  LOAD_DUPLICATE_SCORES,
} from '../../constants/employeeSessionV2';
import {
  storeSessionReports,
  storeAuditLog,
  storeOwnerAuditSettings,
  storeDuplicateScores,
  loadSessionReports,
  sessionReportsFailed,
} from '../../actions/employeeSessionV2';

// Generic post helper that funnels errors through the global error bar.
function* postOrError(url, data) {
  try {
    const response = yield call(() => AuthUtil.postRequest(url, data));
    if (response.status === 200) {
      return response.data;
    }
    yield put(
      showErrorMessage(response.data?.message || SERVER_FAILED_ERROR_MESSAGE)
    );
    return null;
  } catch (error) {
    const msg = error?.response?.data?.message || SERVER_FAILED_ERROR_MESSAGE;
    yield put(showErrorMessage(msg));
    return null;
  }
}

function* loadSessionReportsSaga(action) {
  const data = yield* postOrError(
    EMPLOYEE_SESSION_V2.LOAD_SESSION_REPORTS,
    action.data
  );
  if (data && data.success) {
    yield put(storeSessionReports(data));
  } else {
    // Clear the loading flag so the spinner doesn't hang on after a 504/etc.
    // The error itself has already been surfaced by postOrError.
    yield put(sessionReportsFailed());
  }
}

function* loadAuditLogSaga(action) {
  const data = yield* postOrError(
    EMPLOYEE_SESSION_V2.LOAD_AUDIT_LOG,
    action.data
  );
  if (data && data.success) {
    yield put(storeAuditLog(data.logs || []));
  }
}

function* loadOwnerSettingsSaga(action) {
  const data = yield* postOrError(
    EMPLOYEE_SESSION_V2.LOAD_OWNER_AUDIT_SETTINGS,
    action.data
  );
  if (data && data.success) {
    yield put(storeOwnerAuditSettings(data.settings));
  }
}

function* saveOwnerSettingsSaga(action) {
  const data = yield* postOrError(
    EMPLOYEE_SESSION_V2.SAVE_OWNER_AUDIT_SETTINGS,
    action.data
  );
  if (data && data.success) {
    yield put(storeOwnerAuditSettings(data.settings));
    if (action.callback) action.callback(data);
  }
}

function* loadDuplicateScoresSaga(action) {
  const data = yield* postOrError(
    EMPLOYEE_SESSION_V2.LOAD_DUPLICATE_SCORES,
    action.data
  );
  if (data && data.success) {
    yield put(storeDuplicateScores(data.signatures || []));
  }
}

// Mutation sagas. After a successful mutation we reload the session reports
// so the UI reflects the new state + audit log entry. The callback receives
// the raw server response so the caller can dismiss bottom sheets, etc.
function makeMutationSaga(url) {
  return function* (action) {
    const data = yield* postOrError(url, action.data);
    if (data && data.success && action.data?.clockId && action.data?.userId) {
      yield put(
        loadSessionReports({
          clockId: action.data.clockId,
          userId: action.data.userId,
          locationId: action.data.locationId,
        })
      );
    }
    if (action.callback) action.callback(data);
  };
}

export default function* employeeSessionV2Sagas() {
  yield takeLatest(LOAD_SESSION_REPORTS, loadSessionReportsSaga);
  yield takeLatest(LOAD_AUDIT_LOG, loadAuditLogSaga);
  yield takeLatest(LOAD_OWNER_AUDIT_SETTINGS, loadOwnerSettingsSaga);
  yield takeEvery(SAVE_OWNER_AUDIT_SETTINGS, saveOwnerSettingsSaga);
  yield takeLatest(LOAD_DUPLICATE_SCORES, loadDuplicateScoresSaga);

  yield takeEvery(
    EDIT_BONUS_V2,
    makeMutationSaga(EMPLOYEE_SESSION_V2.EDIT_BONUS)
  );
  yield takeEvery(
    DELETE_BONUS_V2,
    makeMutationSaga(EMPLOYEE_SESSION_V2.DELETE_BONUS)
  );
  yield takeEvery(
    ADD_BONUS_V2,
    makeMutationSaga(EMPLOYEE_SESSION_V2.ADD_BONUS)
  );
  yield takeEvery(
    SUBMIT_BONUS_DECISION,
    makeMutationSaga(EMPLOYEE_SESSION_V2.BONUS_DECISION)
  );

  yield takeEvery(
    EDIT_RAFFLE_V2,
    makeMutationSaga(EMPLOYEE_SESSION_V2.EDIT_RAFFLE)
  );
  yield takeEvery(
    DELETE_RAFFLE_V2,
    makeMutationSaga(EMPLOYEE_SESSION_V2.DELETE_RAFFLE)
  );
  yield takeEvery(
    ADD_RAFFLE_V2,
    makeMutationSaga(EMPLOYEE_SESSION_V2.ADD_RAFFLE)
  );
  yield takeEvery(
    SUBMIT_RAFFLE_DECISION,
    makeMutationSaga(EMPLOYEE_SESSION_V2.RAFFLE_DECISION)
  );

  yield takeEvery(
    EDIT_TICKETOUT_V2,
    makeMutationSaga(EMPLOYEE_SESSION_V2.EDIT_TICKETOUT)
  );
  yield takeEvery(
    DELETE_TICKETOUT_V2,
    makeMutationSaga(EMPLOYEE_SESSION_V2.DELETE_TICKETOUT)
  );

  yield takeEvery(
    EDIT_TRANSACTION_V2,
    makeMutationSaga(EMPLOYEE_SESSION_V2.EDIT_TRANSACTION)
  );
  yield takeEvery(
    DELETE_TRANSACTION_V2,
    makeMutationSaga(EMPLOYEE_SESSION_V2.DELETE_TRANSACTION)
  );
  yield takeEvery(
    ADD_TRANSACTION_V2,
    makeMutationSaga(EMPLOYEE_SESSION_V2.ADD_TRANSACTION)
  );

  yield takeEvery(
    EDIT_CASH_LEFT_V2,
    makeMutationSaga(EMPLOYEE_SESSION_V2.EDIT_CASH_LEFT)
  );
}
